import { SelectActionItem } from '@/types/Select'
import { ComponentType, ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SettingsIcon from '@mui/icons-material/Settings'
import styles from './useDialog.module.scss'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader, { DialogHeaderTarget } from '@/components/_custom/DialogHeader/DialogHeader'
import { DialogActionsTarget } from '@/components/_custom/DialogActions/DialogActions'
import dialogActionsStyles from '@/components/_custom/DialogActions/DialogActions.module.scss'
import { Add } from '@mui/icons-material'

export type UseDialogProps<P extends object> = {
    dialogTitle: string | true | false
    Component: ComponentType<P>
    resourceLabel?: { label: string; type: 'addResource' | 'manageResource' }
    hideDialogClose?: boolean
    onClose?: () => void
    isParent?: boolean
} & (Record<string, never> extends P ? { props?: (handleClose: () => void) => P } : { props: (handleClose: () => void) => P })

type DialogResult = {
    dialog: ReactNode
    reopenTrigger: number
    openDialog: () => void
}

export function useDialog<P extends object>(
    options: UseDialogProps<P> & { resourceLabel: { label: string; type: 'addResource' | 'manageResource' } }
): DialogResult & { openDialogActionItem: SelectActionItem }

export function useDialog<P extends object>(options: UseDialogProps<P>): DialogResult

export function useDialog<P extends object>({
    dialogTitle,
    Component,
    props = () => ({}) as P,
    resourceLabel,
    hideDialogClose = false,
    onClose,
    isParent = false
}: UseDialogProps<P>): DialogResult & { openDialogActionItem?: SelectActionItem } {
    const { t } = useTranslation()
    const resolvedProps: P = props(handleClose)
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [reopenTrigger, setReopenTrigger] = useState<number>(0)
    const [headerTarget, setHeaderTarget] = useState<HTMLDivElement | null>(null)
    const [actionsTarget, setActionsTarget] = useState<HTMLDivElement | null>(null)
    function handleClose(): void {
        setReopenTrigger((key) => key + 1)
        setIsOpen(false)
        onClose?.()
    }

    function openDialog(): void {
        setIsOpen(true)
    }

    const dialog: ReactNode = (
        <Dialog
            open={isOpen}
            handleClose={handleClose}
            maxWidth='md'
            className={styles.ManagedDialog}
            transitionDuration={150}
            transitionEasing='ease-out'
        >
            <div className={styles.Layout}>
                {typeof dialogTitle === 'string' && (
                    <DialogHeaderTarget.Provider value={null}>
                        <DialogHeader title={t(dialogTitle)} parent={isParent} />
                    </DialogHeaderTarget.Provider>
                )}
                {dialogTitle === true && <div ref={setHeaderTarget} />}
                <DialogBody className={styles.ScrollableBody}>
                    <DialogHeaderTarget.Provider value={dialogTitle === true ? headerTarget : null}>
                        <DialogActionsTarget.Provider
                            value={{
                                target: actionsTarget,
                                renderWrapper: hideDialogClose,
                                footerClassName: hideDialogClose ? styles.Footer : undefined
                            }}
                        >
                            <Component {...resolvedProps} />
                        </DialogActionsTarget.Provider>
                    </DialogHeaderTarget.Provider>
                </DialogBody>
                <div
                    className={hideDialogClose ? '' : `${styles.Footer} ${dialogActionsStyles.Actions}`}
                    ref={setActionsTarget}
                >
                    {!hideDialogClose && <ButtonTextIcon type='close' onClick={handleClose} nowrap />}
                </div>
            </div>
        </Dialog>
    )

    const result: DialogResult = { dialog, reopenTrigger, openDialog }

    if (resourceLabel === undefined) {
        return result
    }

    const openDialogActionItem: SelectActionItem = {
        label: t(resourceLabel.type, { resource: t(resourceLabel.label).toLocaleLowerCase() }),
        icon: resourceLabel.type === 'manageResource' ? <SettingsIcon /> : <Add />,
        onClick: openDialog
    }

    return { ...result, openDialogActionItem }
}
